import { Database } from "@punchup/data/supabase.types";
import { SupabaseClient } from "@supabase/supabase-js";
import { logException } from "@/analytics/sentry";

import {
  getUserNotificationPreferences,
  updateNotificationPreferences,
} from "@punchup/data/server";

export { getUserNotificationPreferences, updateNotificationPreferences };

import {
  NotificationPreferences,
  NotificationPreferenceUpdatePayload,
  NotificationPreference,
  NewsletterEmailFrequencyType,
  NotificationContentType,
  NotificationPreferenceRow,
} from "@punchup/data/models";

export {
  type NotificationPreferences,
  type NotificationPreferenceRow,
  type NotificationPreferenceUpdatePayload,
  type NotificationPreference,
  type NewsletterEmailFrequencyType,
  type NotificationContentType,
};

export async function getUserContactInfo(supabase: SupabaseClient<Database>) {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    throw new Error("User not authenticated");
  }

  return {
    hasEmail: !!user?.email,
    hasPhone: !!user?.phone,
  };
}
export const handlePhoneUpdate = async (
  supabase: SupabaseClient<Database>,
  phone: string | null
): Promise<void> => {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { error: updateError } = await supabase
      .from("users_extra")
      .update({
        phone: phone,
      })
      .eq("id", userData.user.id);

    if (updateError) throw updateError;
  } catch (error) {
    console.error("Error updating phone number:", error);
    throw error;
  }
};

export const handlePhoneDelete = async (
  supabase: SupabaseClient<Database>
): Promise<void> => {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { error: deleteError } = await supabase
      .from("users_extra")
      .update({
        phone: null,
      })
      .eq("id", userData.user.id);

    if (deleteError) throw deleteError;
  } catch (error) {
    console.error("Error deleting phone number:", error);
    throw error;
  }
};

export const handleEmailUpdate = async (
  supabase: SupabaseClient<Database>,
  email: string | null
): Promise<void> => {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { error: updateError } = await supabase
      .from("users_extra")
      .update({
        email: email,
      })
      .eq("id", userData.user.id);

    if (updateError) throw updateError;
  } catch (error) {
    console.error("Error updating email:", error);
    throw error;
  }
};

export const handleEmailDelete = async (
  supabase: SupabaseClient<Database>
): Promise<void> => {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { error: deleteError } = await supabase
      .from("users_extra")
      .update({
        email: null,
      })
      .eq("id", userData.user.id);

    if (deleteError) throw deleteError;
  } catch (error) {
    console.error("Error deleting email:", error);
    throw error;
  }
};

export async function unsubscribeFromComedianSMS(
  supabase: SupabaseClient<Database>,
  comedianId: string,
  callerIsServer: boolean = false,
  user_id?: string
): Promise<boolean> {
  let userId = user_id;
  if (!callerIsServer) {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.user) {
      throw new Error(
        "Supabase client must have an active session to update notification preferences."
      );
    }
    userId = session.user.id;
  }

  if (!userId) {
    throw new Error(
      "Unable to read userId when attempting to update notification preferences"
    );
  }

  // First get the current array of unsubscribed comedians
  const { data: currentPrefs, error: fetchError } = await supabase
    .from("notification_preferences_alt")
    .select("comedians_unsubscribed_from_sms")
    .eq("user_id", userId)
    .single();

  if (fetchError) {
    logException(fetchError, {
      data: "Database error while fetching notification preferences",
    });
    return false;
  }

  // Add the new comedian to the array if not already present
  const currentUnsubscribed =
    currentPrefs?.comedians_unsubscribed_from_sms || [];
  const updatedUnsubscribed = Array.from(
    new Set([...currentUnsubscribed, comedianId])
  );

  const { error: updateError } = await supabase
    .from("notification_preferences_alt")
    .update({ comedians_unsubscribed_from_sms: updatedUnsubscribed })
    .eq("user_id", userId);

  if (updateError) {
    logException(updateError, {
      data: "Database error while updating SMS unsubscription preferences",
    });
    return false;
  }

  return true;
}

export async function unsubscribeFromComedianEmail(
  supabase: SupabaseClient<Database>,
  comedianId: string,
  callerIsServer: boolean = false,
  user_id?: string
): Promise<boolean> {
  let userId = user_id;
  if (!callerIsServer) {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.user) {
      throw new Error(
        "Supabase client must have an active session to update notification preferences."
      );
    }
    userId = session.user.id;
  }

  if (!userId) {
    throw new Error(
      "Unable to read userId when attempting to update notification preferences"
    );
  }

  // First get the current array of unsubscribed comedians
  const { data: currentPrefs, error: fetchError } = await supabase
    .from("notification_preferences_alt")
    .select("comedians_unsubscribed_from_emails")
    .eq("user_id", userId)
    .single();

  if (fetchError) {
    logException(fetchError, {
      data: "Database error while fetching notification preferences",
    });
    return false;
  }

  // Add the new comedian to the array if not already present
  const currentUnsubscribed =
    currentPrefs?.comedians_unsubscribed_from_emails || [];
  const updatedUnsubscribed = Array.from(
    new Set([...currentUnsubscribed, comedianId])
  );

  const { error: updateError } = await supabase
    .from("notification_preferences_alt")
    .update({ comedians_unsubscribed_from_emails: updatedUnsubscribed })
    .eq("user_id", userId);

  if (updateError) {
    logException(updateError, {
      data: "Database error while updating email unsubscription preferences",
    });
    return false;
  }

  return true;
}
export async function getComedianNotificationPreference(
  supabase: SupabaseClient<Database>,
  comedianId: string,
  callerIsServer: boolean = false,
  user_id?: string
): Promise<NotificationPreference> {
  let userId = user_id;
  if (!callerIsServer) {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.user) {
      throw new Error(
        "Supabase client must have an active session to get notification preferences."
      );
    }
    userId = session.user.id;
  }

  if (!userId) {
    throw new Error(
      "Unable to read userId when attempting to fetch notification preferences"
    );
  }

  // Get the unsubscription arrays and SMS opt-in flag for this user
  const { data: currentPrefs, error: fetchError } = await supabase
    .from("notification_preferences_alt")
    .select(
      "comedians_unsubscribed_from_sms, comedians_unsubscribed_from_emails, has_opted_in_sms_custom_messaging"
    )
    .eq("user_id", userId)
    .single();

  if (fetchError) {
    logException(fetchError, {
      data: "Database error while fetching comedian notification preferences",
    });
    // Default to "all" if we can't fetch preferences
    return "all";
  }

  const smsUnsubscribed = currentPrefs?.comedians_unsubscribed_from_sms || [];
  const emailUnsubscribed =
    currentPrefs?.comedians_unsubscribed_from_emails || [];
  const hasOptedInSMS =
    currentPrefs?.has_opted_in_sms_custom_messaging || false;

  const isUnsubscribedFromSMS = smsUnsubscribed.includes(comedianId);
  const isUnsubscribedFromEmail = emailUnsubscribed.includes(comedianId);

  // If user hasn't opted into SMS custom messaging, they're effectively unsubscribed from SMS
  const effectivelyUnsubscribedFromSMS =
    isUnsubscribedFromSMS || !hasOptedInSMS;

  // Determine the preference based on unsubscription status
  if (effectivelyUnsubscribedFromSMS && isUnsubscribedFromEmail) {
    return "none";
  } else if (isUnsubscribedFromEmail) {
    // Even if we return "sms", the UI should disable SMS options if hasOptedInSMS is false
    return "sms";
  } else if (effectivelyUnsubscribedFromSMS) {
    return "email";
  } else {
    return "all";
  }
}

function determinePreferenceFromState(
  isUnsubscribedFromSMS: boolean,
  isUnsubscribedFromEmail: boolean
): NotificationPreference {
  if (isUnsubscribedFromSMS && isUnsubscribedFromEmail) {
    return "none";
  } else if (isUnsubscribedFromEmail) {
    return "sms";
  } else if (isUnsubscribedFromSMS) {
    return "email";
  } else {
    return "all";
  }
}

export async function updateComedianNotificationPreference(
  supabase: SupabaseClient<Database>,
  comedianId: string,
  preference: NotificationPreference,
  callerIsServer: boolean = false,
  user_id?: string
): Promise<boolean> {
  let userId = user_id;
  if (!callerIsServer) {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.user) {
      throw new Error(
        "Supabase client must have an active session to update notification preferences."
      );
    }
    userId = session.user.id;
  }

  if (!userId) {
    throw new Error(
      "Unable to read userId when attempting to update notification preferences"
    );
  }

  try {
    // Fetch current unsubscribed arrays
    const { data: currentPrefs, error: fetchError } = await supabase
      .from("notification_preferences_alt")
      .select(
        "comedians_unsubscribed_from_sms, comedians_unsubscribed_from_emails"
      )
      .eq("user_id", userId)
      .single();

    if (fetchError) {
      logException(fetchError, {
        data: "Database error while fetching comedian notification preferences",
      });
      return false;
    }

    const smsUnsubscribed = currentPrefs?.comedians_unsubscribed_from_sms || [];
    const emailUnsubscribed =
      currentPrefs?.comedians_unsubscribed_from_emails || [];

    const isUnsubscribedFromSMS = smsUnsubscribed.includes(comedianId);
    const isUnsubscribedFromEmail = emailUnsubscribed.includes(comedianId);

    const currentPreference = determinePreferenceFromState(
      isUnsubscribedFromSMS,
      isUnsubscribedFromEmail
    );

    // No change needed
    if (preference === currentPreference) {
      return true;
    }

    const getUpdater = () => {
      switch (preference) {
        case "all":
          return {
            comedians_unsubscribed_from_sms: smsUnsubscribed.filter(
              (id) => id !== comedianId
            ),
            comedians_unsubscribed_from_emails: emailUnsubscribed.filter(
              (id) => id !== comedianId
            ),
          };
        case "sms":
          return {
            comedians_unsubscribed_from_sms: smsUnsubscribed.filter(
              (id) => id !== comedianId
            ),
            comedians_unsubscribed_from_emails: Array.from(
              new Set([...emailUnsubscribed, comedianId])
            ),
          };
        case "email":
          return {
            comedians_unsubscribed_from_sms: Array.from(
              new Set([...smsUnsubscribed, comedianId])
            ),
            comedians_unsubscribed_from_emails: emailUnsubscribed.filter(
              (id) => id !== comedianId
            ),
          };
        case "none":
          return {
            comedians_unsubscribed_from_sms: Array.from(
              new Set([...smsUnsubscribed, comedianId])
            ),
            comedians_unsubscribed_from_emails: Array.from(
              new Set([...emailUnsubscribed, comedianId])
            ),
          };
        default:
          return null;
      }
    };
    const updatePayload = getUpdater();
    if (!updatePayload) {
      return false;
    }

    const { error } = await supabase
      .from("notification_preferences_alt")
      .update(updatePayload)
      .eq("user_id", userId);

    if (error) {
      logException(error, {
        data: "Database error while updating comedian notification preference",
      });
      return false;
    }

    return true;
  } catch (error) {
    logException(error, {
      data: "Database error while updating comedian notification preference",
    });
    return false;
  }
}

export async function getUserNotificationData(
  supabase: SupabaseClient<Database>
) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user) {
    return null;
  }
  const userId = session.user.id;

  const [userExtraRes, prefsRes] = await Promise.all([
    supabase
      .from("users_extra")
      .select("email, phone")
      .eq("id", userId)
      .single(),
    supabase
      .from("notification_preferences_alt")
      .select("has_opted_in_sms_custom_messaging, newsletter_email_frequency")
      .eq("user_id", userId)
      .single(),
  ]);

  if (userExtraRes.error) {
    logException(userExtraRes.error);
  }
  if (prefsRes.error) {
    logException(prefsRes.error);
  }

  return {
    contactInfo: {
      hasEmail: !!session.user.email,
      hasPhone: !!session.user.phone,
      email: userExtraRes.data?.email,
      phone: userExtraRes.data?.phone,
    },
    smsOptInStatus: prefsRes.data?.has_opted_in_sms_custom_messaging ?? false,
    emailPreferences: prefsRes.data
      ? {
          newsletter_email_frequency: prefsRes.data.newsletter_email_frequency,
        }
      : null,
  };
}
